/**
 * The `process` global, for the browser. stdout/stderr are routed to whichever terminal is running
 * a command; `exit` throws so a command that would end the process ends the command instead.
 */
export class ExitSignal extends Error {
  constructor(readonly code: number) {
    super(`exit ${code}`)
  }
}
export const sink = { out: (_s: string): void => {}, err: (_s: string): void => {} }
/**
 * Set while an async command (one `main()` handed to a promise by returning STAY_ALIVE) is running:
 * its `process.exit(rc)` then ends the command instead of throwing out of a promise callback.
 */
export const exitHook: { fn: ((code: number) => void) | undefined } = { fn: undefined }
export const process = {
  env: { USER: "demo", HOME: "/home/demo", PATH: "/usr/bin" } as Record<string, string | undefined>,
  argv: ["node", "sibyl"],
  platform: "linux",
  pid: 4242,
  version: "v20.18.0",
  versions: { node: "20.18.0" },
  exitCode: undefined as number | undefined,
  cwd: (): string => "/work",
  exit(code?: number): never {
    if (exitHook.fn !== undefined) {
      exitHook.fn(code ?? 0)
      return undefined as never
    }
    throw new ExitSignal(code ?? 0)
  },
  stdout: { write: (s: string): boolean => (sink.out(String(s)), true), isTTY: false, columns: 96 },
  stderr: { write: (s: string): boolean => (sink.err(String(s)), true), isTTY: false },
  on(): void {},
  nextTick(fn: () => void): void {
    queueMicrotask(fn)
  },
}

/** Just enough `Buffer` for src/web/server.ts's request-body reader. */
function withText(u: Uint8Array): Uint8Array & { toString(enc?: string): string } {
  const b = u as Uint8Array & { toString(enc?: string): string }
  b.toString = () => new TextDecoder().decode(u)
  return b
}
export const Buffer = {
  concat(chunks: Uint8Array[]): Uint8Array & { toString(enc?: string): string } {
    const out = new Uint8Array(chunks.reduce((n, c) => n + c.length, 0))
    let o = 0
    for (const c of chunks) {
      out.set(c, o)
      o += c.length
    }
    return withText(out)
  },
  from(s: string | Uint8Array): Uint8Array & { toString(enc?: string): string } {
    return withText(typeof s === "string" ? new TextEncoder().encode(s) : s)
  },
  isBuffer: (x: unknown): boolean => x instanceof Uint8Array,
}
